import {
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Checkbox,
} from "@mui/material";
import ZoneGroupIcon from "./ZoneGroupIcon";
import ControlCardActionArea from "./ControlCardActionArea";
import ControlCardTitle from "./ControlCardTitle";

// Styles found in deviceControlStyles.js
// Two buttons side by side on a 150px card: equal halves, text slightly smaller
const splitButtonSx = { minWidth: 0, flex: "1 1 0", px: 0.5, fontSize: "0.75rem" };
// The button(s) look as before; ControlCardActionArea also makes the rest of the
// card clickable: the whole card for one button, left/right half for Stop/Resume.

const ControlCard = ({
  zone,
  language,
  robotStatus,
  t,
  getButtonStatus,
  isButtonDisabled,
  handleButtonClick,
  getButtonBackgroundColor,
  buttonStyles,
  selectionMode = false,
  isSelected = false,
  selectionOrder = null,
  onToggleSelect,
  forceDisabled = false,
}) => {
  const buttonDisabled = forceDisabled || isButtonDisabled(zone);
  const buttonStatus = getButtonStatus(zone);
  const zoneName = zone.name
    ? language === "en"
      ? zone.nameEn.toUpperCase()
      : zone.name.toUpperCase()
    : "UNKNOWN zone";

  // What a click on the card (or each half of it) does
  let cardActions;
  if (selectionMode) {
    cardActions = [
      { key: "select", onClick: () => onToggleSelect?.(zone.id) },
    ];
  } else if (buttonStatus === "paused") {
    cardActions = [
      {
        key: "stop",
        disabled: buttonDisabled,
        onClick: () => handleButtonClick(zone.id, zone.name, "stop"),
      },
      {
        key: "resume",
        disabled: buttonDisabled,
        onClick: () => handleButtonClick(zone.id, zone.name, "resume"),
      },
    ];
  } else {
    cardActions = [
      {
        key: buttonStatus,
        disabled: buttonDisabled,
        onClick: () => handleButtonClick(zone.id, zone.name, buttonStatus),
      },
    ];
  }

  return (
    <Grid item key={zone.id}>
      <Card
        title={zoneName}
        sx={{
          height: 150,
          width: 150,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          border: isSelected ? "2px solid #1976d2" : "2px solid transparent",
          boxShadow: isSelected ? 6 : 1,
          transform: isSelected ? "scale(1.01)" : "scale(1)",
          transition: "all 0.2s ease",
          ...(selectionMode
            ? {
                animation:
                  "zoneSelectionShake 0.5s ease-in-out infinite alternate",
                "@keyframes zoneSelectionShake": {
                  "0%": { transform: "rotate(-0.6deg)" },
                  "100%": { transform: "rotate(0.6deg)" },
                },
              }
            : {}),
        }}
      >
        {(selectionMode || isSelected) && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: 999,
              px: 0.5,
              py: 0.25,
              // Clicks go to the card underneath (toggles the selection)
              pointerEvents: "none",
            }}
          >
            {selectionMode ? (
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{ p: 0.25 }}
                tabIndex={-1}
              />
            ) : null}
            {isSelected && (
              <Box
                sx={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                {selectionOrder}
              </Box>
            )}
          </Box>
        )}

        <ControlCardActionArea actions={cardActions}>
          <CardContent
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              p: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <ZoneGroupIcon
                groupName={zone.nameEn}
                displayName={zone.name}
                size={18}
                sx={{ mr: 1 }}
              />
            </Box>

            <ControlCardTitle name={zoneName} />

            {selectionMode ? (
              <Button
                variant="outlined"
                fullWidth
                disabled
                sx={{ opacity: 0.9 }}
              >
                {isSelected
                  ? t("buttonStatus.selected").toUpperCase()
                  : t("buttonStatus.tapSelect").toUpperCase()}
              </Button>
            ) : buttonStatus === "paused" ? (
              // Show both Stop and Resume buttons when paused
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={buttonDisabled}
                  onClick={() => handleButtonClick(zone.id, zone.name, "stop")}
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor: buttonDisabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("stop"),
                    "&:hover": {
                      backgroundColor: buttonDisabled
                        ? "#9e9e9e"
                        : getButtonBackgroundColor("stop"),
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  {t("buttonStatus.stop")}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={buttonDisabled}
                  onClick={() =>
                    handleButtonClick(zone.id, zone.name, "resume")
                  }
                  sx={{
                    ...buttonStyles.base,
                    ...splitButtonSx,
                    backgroundColor: buttonDisabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor("resume"),
                    "&:hover": {
                      backgroundColor: buttonDisabled
                        ? "#9e9e9e"
                        : getButtonBackgroundColor("resume"),
                      ...buttonStyles.hover,
                    },
                  }}
                >
                  {t("buttonStatus.resume")}
                </Button>
              </Box>
            ) : (
              // Show single button for all other statuses
              <Button
                variant="outlined"
                fullWidth
                disabled={buttonDisabled}
                onClick={() =>
                  handleButtonClick(zone.id, zone.name, buttonStatus)
                }
                sx={{
                  ...buttonStyles.base,
                  backgroundColor: buttonDisabled
                    ? "#9e9e9e"
                    : getButtonBackgroundColor(buttonStatus),
                  "&:hover": {
                    backgroundColor: buttonDisabled
                      ? "#9e9e9e"
                      : getButtonBackgroundColor(buttonStatus),
                    ...buttonStyles.hover,
                  },
                }}
              >
                {t(`buttonStatus.${buttonStatus}`)}
              </Button>
            )}
          </CardContent>
        </ControlCardActionArea>
      </Card>
    </Grid>
  );
};

export default ControlCard;
